package org.zq.astrock_ai.ui.research.gushi.data

import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.zq.astrock_ai.api.RecommendBoards
import org.zq.astrock_ai.api.RecommendStyle
import org.zq.astrock_ai.api.dto.RecommendResponseDto
import org.zq.astrock_ai.storage.ResearchKvStorage
import org.zq.astrock_ai.util.currentTimeMillis

/**
 * 今日荐股完整结果缓存。
 * 当前前端开关关闭读取，但每次分析仍会 [save]，便于以后恢复「同日同市场直接展示」。
 */
object RecommendTodayStore {
    private const val KEY = "recommend.today.v1"
    private const val MAX_SLOTS = 8
    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = true
    }

    @Serializable
    private data class Slot(
        val day: Int = 0,
        val boards: String = "",
        val style: String = "",
        val result: RecommendResponseDto = RecommendResponseDto(),
    )

    @Serializable
    private data class File(
        val version: Int = 1,
        val slots: List<Slot> = emptyList(),
    )

    fun key(boards: Set<String>, style: String, nowMs: Long = currentTimeMillis()): String {
        val day = chinaEpochDay(nowMs)
        val b = RecommendBoards.encode(RecommendBoards.normalizeSelection(boards))
        val s = RecommendStyle.parse(style)
        return "$day|$b|$s"
    }

    fun save(boards: Set<String>, style: String, result: RecommendResponseDto) {
        val now = currentTimeMillis()
        val day = chinaEpochDay(now)
        val b = RecommendBoards.encode(RecommendBoards.normalizeSelection(boards))
        val s = RecommendStyle.parse(style)
        val slot = Slot(day = day, boards = b, style = s, result = result)
        val others = loadFile().slots
            .filterNot { it.day == day && it.boards == b && it.style == s }
            .filter { it.day >= day - 1 } // 只留今天/昨天，避免 KV 膨胀
        val next = (listOf(slot) + others).take(MAX_SLOTS)
        ResearchKvStorage.put(KEY, json.encodeToString(File(slots = next)))
    }

    fun loadToday(boards: Set<String>, style: String): RecommendResponseDto? {
        val now = currentTimeMillis()
        val day = chinaEpochDay(now)
        val b = RecommendBoards.encode(RecommendBoards.normalizeSelection(boards))
        val s = RecommendStyle.parse(style)
        return loadFile().slots.firstOrNull {
            it.day == day && it.boards == b && it.style == s
        }?.result
    }

    private fun loadFile(): File {
        val raw = ResearchKvStorage.get(KEY)?.trim().orEmpty()
        if (raw.isBlank()) return File()
        return runCatching { json.decodeFromString<File>(raw) }.getOrDefault(File())
    }
}
